import React, { useEffect, useState } from "react";
import {
  Code2,
  Cpu,
  Layers,
  Boxes,
  Database,
  Ship,
  Palette,
  Coffee,
} from "lucide-react";
import {
  SiReact,
  SiNodedotjs,
  SiPostgresql,
  SiMongodb,
  SiExpress,
  SiJavascript,
  SiHtml5,
  SiCss3,
  SiTailwindcss,
  SiFigma,
  SiVercel,
  SiPostman,
  SiTypescript,
} from "react-icons/si";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import AWSIcon from "react-aws-icons/dist/aws/logo/AWS";

import AIAssistant from "./AIBot";
import ResumePage from "./pages/ResumePage";
import ViewProject from "./pages/ViewAllProjects";
import BookCall from "./Components/BookCall";
import AbtMe from "./pages/AboutMe";
import ContactForm from "./pages/contactForm";
import SkillsPage from "./pages/skillPage";
import ExperiencePage from "./pages/expSection";
import ProjectsPage from "./pages/projectSection";
import HeroSection from "./pages/heroSection";
import Footer from "./pages/footerSection";
import Loader from "./Components/Loader";
import Header from "./Components/Header";
import Flappy from "./Components/FlappySection";
import WorkExperiencePage from "./pages/WorkExperiencePage";

const Portfolio = () => {
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("darkMode") === "true";
    }
    return false;
  });

  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  // Projects data
  const projects = [
    {
      title: "Travel Website",
      desc: "Responsive travel booking platform with interactive animations and user-friendly UI.",
      tech: ["React", "Node.js", "CSS"],
      live: "",
      code: "https://github.com/ashishkarche/travel-agency",
      image: "/assets/images/travel.png",

      problem:
        "Booking travel packages online was not visually engaging and lacked responsiveness.",
      solution:
        "Designed and developed an interactive, responsive travel website with smooth animations.",
      result:
        "Enhanced user experience with increased engagement and easier booking workflow.",
      status: "working", 

    },
    {
      title: "ChotuLink — URL Shortener",
      desc: "A modern URL shortening app with QR code generation and analytics dashboard for tracking clicks.",
      tech: ["React", "Node.js", "PostgreSQL", "Express"],
      live: "https://chotu-link-t7lr.vercel.app/",
      code: "https://github.com/ashishkarche/chotu-link",
      image: "/assets/images/chotulink.png",
      problem: "Long URLs are hard to share and track for marketing purposes.",
      solution:
        "Built a platform to shorten URLs, generate QR codes, and track analytics in real-time.",
      result:
        "Users can create short links and QR codes with analytics, improving shareability and tracking.",
      status: "building", 
    },
    {
      title: "Structural Audit Management System",
      desc: "Manage building audits by uploading drawings, recording observations, and generating PDF reports automatically.",
      tech: ["React", "Node.js", "PostgreSQL"],
      live: "https://structural-audit-6xw4.vercel.app/",
      code: "https://github.com/ashishkarche/structural-audit",
      image: "/assets/images/audit.png",

      problem:
        "Manual audit processes were time-consuming and prone to errors.",
      solution:
        "Developed a digital system to upload drawings, log observations, and generate reports instantly.",
      result:
        "Audit teams can now complete inspections faster and more accurately, saving hours per project.",
      status: "building", 

    },
    {
      title: "Disease Prediction Site",
      desc: "AI-assisted platform that predicts potential diseases based on user inputs and generates detailed reports.",
      tech: ["React", "Node.js", "PostgreSQL", "Python"],
      live: "https://disease-prediction-ha6l.vercel.app/",
      code: "https://github.com/ashishkarche/disease-prediction",
      image: "/assets/images/disese.png",

      problem:
        "People need a quick preliminary health assessment before consulting doctors.",
      solution:
        "Integrated machine learning models to analyze user inputs and provide predictions with guidance.",
      result:
        "Users receive AI-assisted health insights and reports, improving early detection awareness.",
      status: "building", 

    },
    
    {
      title: "Secure File App",
      desc: "Encrypt and share files securely in the cloud with access control features.",
      tech: ["React", "Docker", "Java", "Springboot"],
      live: "https://group-no-10.vercel.app/",
      code: "https://github.com/ashishkarche/SecureFileApp",
      image: "/assets/images/fileapp.png",
      problem:
        "Sharing sensitive files over cloud without proper encryption was risky.",
      solution:
        "Built a secure file-sharing platform with encryption and role-based access control.",
      result:
        "Users can safely share files with confidence, ensuring privacy and security compliance.",
      status: "building", 

    },
  ];


  const LangIcon = {
    React: <SiReact className="text-sky-500 text-lg" />,
    "Node.js": <SiNodedotjs className="text-green-600 text-lg" />,
    PostgreSQL: <SiPostgresql className="text-blue-600 text-lg" />,
    Express: <Code2 size={18} className="text-gray-700 dark:text-gray-300" />,
    Python: <Cpu size={18} className="text-yellow-500" />,
    CSS: <Code2 size={18} className="text-blue-500" />,
    Docker: <Code2 size={18} className="text-blue-400" />,
    Java: <Code2 size={18} className="text-red-500" />,
    Springboot: <Code2 size={18} className="text-green-500" />,
  };

  const techIcons = {
    All: <Layers size={14} />,
    React: <Boxes size={14} />,
    "Node.js": <Cpu size={14} />,
    PostgreSQL: <Database size={14} />,
    Python: <Code2 size={14} />,
    Docker: <Ship size={14} />,
    CSS: <Palette size={14} />,
    Java: <Coffee size={14} />,
  };

  const experience = [
    {
      company: "CodeNucleus",
      role: "Full-stack Developer — MERN",
      date: "February 2025 — April 2025",
      location: "Pune, Maharashtra, India",
      logo: "/assets/images/codenulce.png",
      linkedin: "http://linkedin.com/",
      github: "http://github.com/",
      twitter: "http://x.com/",
      technologies: [
        { name: "React", icon: <SiReact />, link: "https://reactjs.org/" },
        { name: "Node.js", icon: <SiNodedotjs />, link: "https://nodejs.org/" },
        {
          name: "MongoDB",
          icon: <SiMongodb />,
          link: "https://www.mongodb.com/",
        },
        {
          name: "Express",
          icon: <SiExpress />,
          link: "https://expressjs.com/",
        },
        {
          name: "JavaScript",
          icon: <SiJavascript />,
          link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript",
        },
        {
          name: "HTML5",
          icon: <SiHtml5 />,
          link: "https://developer.mozilla.org/en-US/docs/Web/HTML",
        },
        {
          name: "CSS3",
          icon: <SiCss3 />,
          link: "https://developer.mozilla.org/en-US/docs/Web/CSS",
        },
        {
          name: "Tailwind CSS",
          icon: <SiTailwindcss />,
          link: "https://tailwindcss.com/",
        },
      ],
      points: [
        "Built interactive UI components improving workflow efficiency by ~40%.",
        "Optimized API and DB queries reducing data fetch time by ~30%.",
      ],
    },
    {
      company: "Let's Grow More",
      role: "Junior Frontend Developer (Internship)",
      date: "March 2023 — April 2023",
      location: "Pune, Maharashtra, India",
      logo: "/assets/images/letsgrowmore.png",
      linkedin: "http://linkedin.com/",
      github: "http://github.com/",
      twitter: "http://x.com/",
      technologies: [
        { name: "React", icon: <SiReact />, link: "https://reactjs.org/" },
        {
          name: "TypeScript",
          icon: <SiTypescript />,
          link: "https://www.typescriptlang.org/",
        },
        {
          name: "Tailwind CSS",
          icon: <SiTailwindcss />,
          link: "https://tailwindcss.com/",
        },
        { name: "Figma", icon: <SiFigma />, link: "https://www.figma.com/" },
        { name: "Vercel", icon: <SiVercel />, link: "https://vercel.com/" },
        {
          name: "Postman",
          icon: <SiPostman />,
          link: "https://www.postman.com/",
        },
        { name: "AWS", icon: <AWSIcon />, link: "https://aws.amazon.com/" },
      ],
      points: [
        "Delivered responsive, mobile-first interfaces.",
        "Built a registration form with client-side validation.",
      ],
    },
  ];
  const skills = [
    "HTML",
    "CSS",
    "JavaScript",
    "React",
    "Node.js",
    "Express",
    "PostgreSQL",
    "MySQL",
    "Docker",
    "Git",
    "Vercel",
    "Postman",
  ];

  return (
    <Router>
      {!hasLoaded && <Loader setHasLoaded={setHasLoaded} />}
      <AIAssistant />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-slate-800 dark:text-gray-100 antialiased transition-colors duration-300">
        <Header darkMode={darkMode} setDarkMode={setDarkMode} />

        <Routes>
          {/* Home Page */}
          <Route
            path="/"
            element={
              <main className="max-w-5xl mx-auto px-6 pb-10">
                <HeroSection />
                <Flappy />
                <ExperiencePage experience={experience} />
                <ProjectsPage
                  projects={projects}
                  techIcons={techIcons}
                  LangIcon={LangIcon}
                />
                <SkillsPage skills={skills} />
                <AbtMe />
                <BookCall />
              </main>
            }
          />

          {/* Contact Page */}
          <Route
            path="/contact"
            element={
              <main className="max-w-5xl mx-auto px-6 pb-20">
                <ContactForm />
              </main>
            }
          />
          <Route path="/resume" element={<ResumePage />} />
          <Route
            path="/work-experience"
            element={
              <main className="max-w-5xl mx-auto px-6 pb-20">
                <WorkExperiencePage experience={experience} />
              </main>
            }
          />
          <Route
            path="/view-all-projects"
            element={
              <main className="max-w-5xl mx-auto px-6 pb-20">
                <ViewProject projects={projects} />
              </main>
            }
          />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
};

export default Portfolio;
